import { Box, Col, FormGroup, Row, Text } from '@ksyos/design-system';
import { omit } from 'lodash';
import { Component, Fragment } from 'react';
import type { ITooProcedureCustomControlData } from '@common/validation/customControls/ophthalmology';
import type { ValidationErrors } from '@frontend/utils/validation';
import { tooV1ProcedureInitializeCustomControlData } from '@common/customControls/tooV1ProcedureInitializeCustomControlData';
import { TooAmslerTest, TooGonioscope, TooProtocol } from '@common/validation/customControls/ophthalmology';
import { SearchableEnumDropdown } from '@frontend/components/elements/SearchableEnumDropdown';
import { translateKey } from '@frontend/translate';
import type { IProcedureAddEdit } from '../../../procedures/ProcedureItemAddEdit';
import type { TooV1ProcedureRequiredFields } from './TooV1ProcedureDiagnosticReportAddEdit';
import { RoundedCheckMarkButton } from '../../../../elements/buttons/roundedCheckMarkButton/RoundedCheckMarkButton';
import { InputTextArea, NullableInputText, StyledBorderAndPadding } from '../../../../elements/common';
import { NullableTextField } from '../../../../fields/NullableText';
import { TooExaminationOrder } from './tooExaminationOrder';

interface ITooExtraExaminationSelectorProps {
    customControlData: ITooProcedureCustomControlData;
    errors: ValidationErrors;
    handleChange: <K extends keyof IProcedureAddEdit>(name: K, value: IProcedureAddEdit[K]) => Promise<void>;
    handleChangeCustomControl: (values: Partial<ITooProcedureCustomControlData>) => Promise<void>;
    isSubmitted: boolean;
    procedure: IProcedureAddEdit;
    protocol: TooProtocol;
    requiredFields?: Partial<TooV1ProcedureRequiredFields>;
    validateOne: (name: keyof IProcedureAddEdit) => Promise<void>;
    validateOneCustomControl: <K extends keyof ITooProcedureCustomControlData>(
        name: K,
        index?: number
    ) => Promise<void>;
}

interface ITooExtraExamination {
    aanvullend_onderzoek: boolean;
    amsler_test: boolean;
    autorefractie: boolean;
    break_up_time: boolean;
    cycloplegische: boolean;
    eigen_correctie: boolean;
    gonioscopie: boolean;
    nabije_visus: boolean;
    pachymetrie: boolean;
}

interface ITooExtraExaminationSelectorState {
    additionalExaminations: (string | null)[];
    examinations: ITooExtraExamination;
    examinationsLinkedToReport: Partial<ITooExtraExamination>;
}

export class TooExtraExaminationSelector extends Component<
    ITooExtraExaminationSelectorProps,
    ITooExtraExaminationSelectorState
> {
    public constructor(props: ITooExtraExaminationSelectorProps) {
        super(props);

        this.state = {
            examinations: this.getExaminationActiveStatus(),
            examinationsLinkedToReport: this.getExaminationLinkedWithReportActiveStatus(),
            additionalExaminations: [...this.props.procedure.customControlData.aanvullend_onderzoek],
        };
    }

    public async componentDidMount() {
        await this.resetOldAmslerTestValues();
    }

    public async componentDidUpdate(prevProps: ITooExtraExaminationSelectorProps) {
        if (
            prevProps.protocol !== this.props.protocol ||
            JSON.stringify(prevProps.requiredFields || null) !== JSON.stringify(this.props.requiredFields || null)
        ) {
            let requiredExaminationsOpen = this.state.examinationsLinkedToReport;
            if (prevProps.protocol !== this.props.protocol) {
                requiredExaminationsOpen = this.checkRequiredExaminationsFromProtocol(this.props.protocol);
            }
            await this.setState({
                examinations: this.getExaminationActiveStatus(),
                examinationsLinkedToReport: requiredExaminationsOpen,
            });
        }

        if (this.props.procedure.remark !== null && !this.anyOptionChecked()) {
            await this.props.handleChange('remark', null);
        }

        if (prevProps.isSubmitted !== this.props.isSubmitted && this.props.isSubmitted) {
            await this.setState({ examinationsLinkedToReport: this.getExaminationLinkedWithReportActiveStatus() });
        }

        if (
            this.state.additionalExaminations.length < 10 &&
            this.state.additionalExaminations[this.state.additionalExaminations.length - 1] !== null
        ) {
            await this.setState((prevState) => ({
                additionalExaminations: [...prevState.additionalExaminations, null],
            }));
        }
    }

    public render() {
        return (
            <>
                <FormGroup label="Onderzoek toevoegen">
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinations.eigen_correctie}
                            onClick={async () => await this.handleExaminationSelected('eigen_correctie')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Eigen correctie
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinations.autorefractie}
                            onClick={async () => await this.handleExaminationSelected('autorefractie')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Autorefractie
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinations.cycloplegische}
                            onClick={async () => await this.handleExaminationSelected('cycloplegische')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Cyclorefractie
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinationsLinkedToReport.nabije_visus}
                            onClick={async () => await this.handleExaminationSelected('nabije_visus')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Nabij visus
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinations.break_up_time}
                            onClick={async () => await this.handleExaminationSelected('break_up_time')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            BUT test
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinationsLinkedToReport.amsler_test}
                            onClick={async () => await this.handleExaminationSelected('amsler_test')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Amsler test
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinationsLinkedToReport.pachymetrie}
                            onClick={async () => await this.handleExaminationSelected('pachymetrie')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Pachymetrie
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinationsLinkedToReport.gonioscopie}
                            onClick={async () => await this.handleExaminationSelected('gonioscopie')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Gonioscopie
                        </RoundedCheckMarkButton>
                        <RoundedCheckMarkButton
                            icon={'add'}
                            isChecked={this.state.examinations.aanvullend_onderzoek}
                            onClick={async () => await this.handleExaminationSelected('aanvullend_onderzoek')}
                            tabIndex={TooExaminationOrder.notFocusable}
                        >
                            Ander onderzoek
                        </RoundedCheckMarkButton>
                    </Box>
                </FormGroup>
                {(this.isExtraExaminationSelected() || this.isExtraExaminationOpen()) && (
                    <>
                        {this.isExtraExaminationSelected(
                            'eigen_correctie',
                            'autorefractie',
                            'cycloplegische',
                            'nabije_visus',
                            'break_up_time',
                            'amsler_test',
                            'pachymetrie',
                            'gonioscopie'
                        ) && (
                            <>
                                {this.state.examinations.eigen_correctie && (
                                    <FormGroup
                                        hasErrors={
                                            this.props.errors.has('customControlData.eigen_correctie_sferisch_od') ||
                                            this.props.errors.has('customControlData.eigen_correctie_cylindrish_od') ||
                                            this.props.errors.has('customControlData.eigen_correctie_as_od') ||
                                            this.props.errors.has('customControlData.eigen_correctie_sferisch_os') ||
                                            this.props.errors.has('customControlData.eigen_correctie_cylindrish_os') ||
                                            this.props.errors.has('customControlData.eigen_correctie_as_os')
                                        }
                                        label="Eigen correctie"
                                    >
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <Row columnGap={2}>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                automation="eigenCorrectieSferischOd"
                                                                error={this.props.errors.get(
                                                                    'customControlData.eigen_correctie_sferisch_od'
                                                                )}
                                                                fieldKey="eigen_correctie_sferisch_od"
                                                                label=""
                                                                placeholder="sph"
                                                            />
                                                        </Col>
                                                        <Col span={4}>
                                                            <Box
                                                                alignItems="center"
                                                                display="flex"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    fontWeight="bold"
                                                                    mr={1}
                                                                    size="sm"
                                                                >
                                                                    -
                                                                </Text>
                                                                <NullableTextField
                                                                    automation="eigenCorrectieCylindrishOd"
                                                                    error={this.props.errors.get(
                                                                        'customControlData.eigen_correctie_cylindrish_od'
                                                                    )}
                                                                    fieldKey="eigen_correctie_cylindrish_od"
                                                                    label=""
                                                                    placeholder="cyl"
                                                                />
                                                            </Box>
                                                        </Col>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                automation="eigenCorrectieAsOd"
                                                                error={this.props.errors.get(
                                                                    'customControlData.eigen_correctie_as_od'
                                                                )}
                                                                fieldKey="eigen_correctie_as_od"
                                                                label=""
                                                                placeholder="ax"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <Row columnGap={2}>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                automation="eigenCorrectieSferischOs"
                                                                error={this.props.errors.get(
                                                                    'customControlData.eigen_correctie_sferisch_os'
                                                                )}
                                                                fieldKey="eigen_correctie_sferisch_os"
                                                                label=""
                                                                placeholder="sph"
                                                            />
                                                        </Col>
                                                        <Col span={4}>
                                                            <Box
                                                                alignItems="center"
                                                                display="flex"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    fontWeight="bold"
                                                                    mr={1}
                                                                    size="sm"
                                                                >
                                                                    -
                                                                </Text>
                                                                <NullableTextField
                                                                    automation="eigenCorrectieCylindrishOs"
                                                                    error={this.props.errors.get(
                                                                        'customControlData.eigen_correctie_cylindrish_os'
                                                                    )}
                                                                    fieldKey="eigen_correctie_cylindrish_os"
                                                                    label=""
                                                                    placeholder="cyl"
                                                                />
                                                            </Box>
                                                        </Col>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                automation="eigenCorrectieAsOs"
                                                                error={this.props.errors.get(
                                                                    'customControlData.eigen_correctie_as_os'
                                                                )}
                                                                fieldKey="eigen_correctie_as_os"
                                                                label=""
                                                                placeholder="ax"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}

                                {this.state.examinations.autorefractie && (
                                    <FormGroup label="Autorefractie">
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <Row columnGap={2}>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.autorefractie_sferisch_od'
                                                                )}
                                                                fieldKey="autorefractie_sferisch_od"
                                                                label=""
                                                                placeholder="sph"
                                                            />
                                                        </Col>
                                                        <Col span={4}>
                                                            <Box
                                                                alignItems="center"
                                                                display="flex"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    fontWeight="bold"
                                                                    mr={1}
                                                                    size="sm"
                                                                >
                                                                    -
                                                                </Text>
                                                                <NullableTextField
                                                                    error={this.props.errors.get(
                                                                        'customControlData.autorefractie_cylindrisch_od'
                                                                    )}
                                                                    fieldKey="autorefractie_cylindrisch_od"
                                                                    label=""
                                                                    placeholder="cyl"
                                                                />
                                                            </Box>
                                                        </Col>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.autorefractie_as_od'
                                                                )}
                                                                fieldKey="autorefractie_as_od"
                                                                label=""
                                                                placeholder="ax"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <Row columnGap={2}>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.autorefractie_sferisch_os'
                                                                )}
                                                                fieldKey="autorefractie_sferisch_os"
                                                                label=""
                                                                placeholder="sph"
                                                            />
                                                        </Col>
                                                        <Col span={4}>
                                                            <Box
                                                                alignItems="center"
                                                                display="flex"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    fontWeight="bold"
                                                                    mr={1}
                                                                    size="sm"
                                                                >
                                                                    -
                                                                </Text>
                                                                <NullableTextField
                                                                    error={this.props.errors.get(
                                                                        'customControlData.autorefractie_cylindrisch_os'
                                                                    )}
                                                                    fieldKey="autorefractie_cylindrisch_os"
                                                                    label=""
                                                                    placeholder="cyl"
                                                                />
                                                            </Box>
                                                        </Col>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.autorefractie_as_os'
                                                                )}
                                                                fieldKey="autorefractie_as_os"
                                                                label=""
                                                                placeholder="ax"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}

                                {this.state.examinations.cycloplegische && (
                                    <FormGroup label="Cycloplegische refractie">
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <Row columnGap={2}>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.cycloplegische_sferisch_od'
                                                                )}
                                                                fieldKey="cycloplegische_sferisch_od"
                                                                label=""
                                                                placeholder="sph"
                                                            />
                                                        </Col>
                                                        <Col span={4}>
                                                            <Box
                                                                alignItems="center"
                                                                display="flex"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    fontWeight="bold"
                                                                    mr={1}
                                                                    size="sm"
                                                                >
                                                                    -
                                                                </Text>
                                                                <NullableTextField
                                                                    error={this.props.errors.get(
                                                                        'customControlData.cycloplegische_cylindrisch_od'
                                                                    )}
                                                                    fieldKey="cycloplegische_cylindrisch_od"
                                                                    label=""
                                                                    placeholder="cyl"
                                                                />
                                                            </Box>
                                                        </Col>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.cycloplegische_as_od'
                                                                )}
                                                                fieldKey="cycloplegische_as_od"
                                                                label=""
                                                                placeholder="ax"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <Row columnGap={2}>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.cycloplegische_sferisch_od'
                                                                )}
                                                                fieldKey="cycloplegische_sferisch_os"
                                                                label=""
                                                                placeholder="sph"
                                                            />
                                                        </Col>
                                                        <Col span={4}>
                                                            <Box
                                                                alignItems="center"
                                                                display="flex"
                                                            >
                                                                <Text
                                                                    as="span"
                                                                    fontWeight="bold"
                                                                    mr={1}
                                                                    size="sm"
                                                                >
                                                                    -
                                                                </Text>
                                                                <NullableTextField
                                                                    error={this.props.errors.get(
                                                                        'customControlData.cycloplegische_cylindrisch_os'
                                                                    )}
                                                                    fieldKey="cycloplegische_cylindrisch_os"
                                                                    label=""
                                                                    placeholder="cyl"
                                                                />
                                                            </Box>
                                                        </Col>
                                                        <Col span={4}>
                                                            <NullableTextField
                                                                error={this.props.errors.get(
                                                                    'customControlData.cycloplegische_as_os'
                                                                )}
                                                                fieldKey="cycloplegische_as_os"
                                                                label=""
                                                                placeholder="ax"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}

                                {this.state.examinationsLinkedToReport.nabije_visus && (
                                    <FormGroup
                                        hasErrors={this.requiredExaminationHasErrors('nabije_visus')}
                                        isRequired={this.isRequiredExamination('nabije_visus')}
                                        label="Nabij visus (onverwijd)"
                                    >
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <NullableTextField
                                                        error={this.props.errors.get(
                                                            'customControlData.nabije_visus_od'
                                                        )}
                                                        fieldKey="nabije_visus_od"
                                                        placeholder=""
                                                    />
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <NullableTextField
                                                        error={this.props.errors.get(
                                                            'customControlData.nabije_visus_os'
                                                        )}
                                                        fieldKey="nabije_visus_os"
                                                        placeholder=""
                                                    />
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}
                                {this.state.examinations.break_up_time && (
                                    <FormGroup label="Break-up time (BUT) test">
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <Box
                                                        alignItems="center"
                                                        display="flex"
                                                    >
                                                        <NullableTextField
                                                            error={this.props.errors.get(
                                                                'customControlData.break_up_time_od'
                                                            )}
                                                            fieldKey="break_up_time_od"
                                                            placeholder=""
                                                        />
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            ml={1}
                                                            size="sm"
                                                        >
                                                            sec.
                                                        </Text>
                                                    </Box>
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <Box
                                                        alignItems="center"
                                                        display="flex"
                                                    >
                                                        <NullableTextField
                                                            error={this.props.errors.get(
                                                                'customControlData.break_up_time_os'
                                                            )}
                                                            fieldKey="break_up_time_os"
                                                            placeholder=""
                                                        />
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            ml={1}
                                                            size="sm"
                                                        >
                                                            sec.
                                                        </Text>
                                                    </Box>
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}

                                {this.state.examinationsLinkedToReport.amsler_test && (
                                    <FormGroup
                                        hasErrors={this.requiredExaminationHasErrors('amsler_test')}
                                        isRequired={this.isRequiredExamination('amsler_test')}
                                        label="Amsler test"
                                    >
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <SearchableEnumDropdown
                                                        error={this.props.errors.get(
                                                            'customControlData.amsler_test_od'
                                                        )}
                                                        jsEnum={TooAmslerTest}
                                                        onChange={async (value) =>
                                                            await this.props.handleChangeCustomControl({
                                                                amsler_test_od: value,
                                                            })
                                                        }
                                                        onDoValidate={async () => {
                                                            await this.props.validateOneCustomControl('amsler_test_od');
                                                        }}
                                                        placeholder="Klik en kies uit de lijst..."
                                                        value={this.props.procedure.customControlData.amsler_test_od}
                                                    />
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <SearchableEnumDropdown
                                                        error={this.props.errors.get(
                                                            'customControlData.amsler_test_os'
                                                        )}
                                                        jsEnum={TooAmslerTest}
                                                        onChange={async (value) =>
                                                            await this.props.handleChangeCustomControl({
                                                                amsler_test_os: value,
                                                            })
                                                        }
                                                        onDoValidate={async () => {
                                                            await this.props.validateOneCustomControl('amsler_test_os');
                                                        }}
                                                        placeholder="Klik en kies uit de lijst..."
                                                        value={this.props.procedure.customControlData.amsler_test_os}
                                                    />
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}
                                {this.state.examinationsLinkedToReport.pachymetrie && (
                                    <FormGroup
                                        hasErrors={this.requiredExaminationHasErrors('pachymetrie')}
                                        isRequired={this.isRequiredExamination('pachymetrie')}
                                        label="Pachymetrie"
                                    >
                                        <Row columnGap={[2, 4, 6]}>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OD
                                                    </Text>
                                                    <Box
                                                        alignItems="center"
                                                        display="flex"
                                                    >
                                                        <NullableTextField
                                                            error={this.props.errors.get(
                                                                'customControlData.pachymetrie_od'
                                                            )}
                                                            fieldKey="pachymetrie_od"
                                                            placeholder=""
                                                        />
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            ml={1}
                                                            size="sm"
                                                        >
                                                            μm
                                                        </Text>
                                                    </Box>
                                                </StyledBorderAndPadding>
                                            </Col>
                                            <Col span={[12, 6, null, null, 4]}>
                                                <StyledBorderAndPadding>
                                                    <Text
                                                        fontWeight="bold"
                                                        size="xs"
                                                    >
                                                        OS
                                                    </Text>
                                                    <Box
                                                        alignItems="center"
                                                        display="flex"
                                                    >
                                                        <NullableTextField
                                                            error={this.props.errors.get(
                                                                'customControlData.pachymetrie_os'
                                                            )}
                                                            fieldKey="pachymetrie_os"
                                                            placeholder=""
                                                        />
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            ml={1}
                                                            size="sm"
                                                        >
                                                            μm
                                                        </Text>
                                                    </Box>
                                                </StyledBorderAndPadding>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                )}
                                {this.state.examinationsLinkedToReport.gonioscopie && (
                                    <>
                                        <FormGroup
                                            hasErrors={this.requiredExaminationHasErrors('gonioscopie')}
                                            isRequired={this.isRequiredExamination('gonioscopie')}
                                            label="Gonioscopie/voorsegment OCT"
                                        >
                                            <Row columnGap={[2, 4, 6]}>
                                                <Col span={[12, 6, null, null, 4]}>
                                                    <StyledBorderAndPadding>
                                                        <Text
                                                            fontWeight="bold"
                                                            size="xs"
                                                        >
                                                            OD
                                                        </Text>
                                                        <SearchableEnumDropdown
                                                            error={this.props.errors.get(
                                                                'customControlData.gonioscopie_od'
                                                            )}
                                                            jsEnum={TooGonioscope}
                                                            onChange={async (value) =>
                                                                await this.props.handleChangeCustomControl({
                                                                    gonioscopie_od: value,
                                                                })
                                                            }
                                                            onDoValidate={async () => {
                                                                await this.props.validateOneCustomControl(
                                                                    'gonioscopie_od'
                                                                );
                                                            }}
                                                            placeholder="Klik en kies uit de lijst..."
                                                            value={
                                                                this.props.procedure.customControlData.gonioscopie_od
                                                            }
                                                        />
                                                    </StyledBorderAndPadding>
                                                </Col>
                                                <Col span={[12, 6, null, null, 4]}>
                                                    <StyledBorderAndPadding>
                                                        <Text
                                                            fontWeight="bold"
                                                            size="xs"
                                                        >
                                                            OS
                                                        </Text>
                                                        <SearchableEnumDropdown
                                                            error={this.props.errors.get(
                                                                'customControlData.gonioscopie_os'
                                                            )}
                                                            jsEnum={TooGonioscope}
                                                            onChange={async (value) =>
                                                                await this.props.handleChangeCustomControl({
                                                                    gonioscopie_os: value,
                                                                })
                                                            }
                                                            onDoValidate={async () => {
                                                                await this.props.validateOneCustomControl(
                                                                    'gonioscopie_os'
                                                                );
                                                            }}
                                                            placeholder="Klik en kies uit de lijst..."
                                                            value={
                                                                this.props.procedure.customControlData.gonioscopie_os
                                                            }
                                                        />
                                                    </StyledBorderAndPadding>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                        <FormGroup
                                            hasErrors={
                                                this.props.errors.has('customControlData.gonioscopie_toelichting_od') ||
                                                this.props.errors.has('customControlData.gonioscopie_toelichting_os')
                                            }
                                        >
                                            <Row columnGap={[2, 4, 6]}>
                                                <Col span={[12, 6, null, null, 4]}>
                                                    <StyledBorderAndPadding>
                                                        <Text
                                                            fontWeight="bold"
                                                            size="xs"
                                                        >
                                                            OD
                                                        </Text>
                                                        <NullableTextField
                                                            error={this.props.errors.get(
                                                                'customControlData.gonioscopie_toelichting_od'
                                                            )}
                                                            expandableTextArea
                                                            fieldKey="gonioscopie_toelichting_od"
                                                            label=""
                                                            placeholder="Toelichting..."
                                                        />
                                                    </StyledBorderAndPadding>
                                                </Col>
                                                <Col span={[12, 6, null, null, 4]}>
                                                    <StyledBorderAndPadding>
                                                        <Text
                                                            fontWeight="bold"
                                                            size="xs"
                                                        >
                                                            OS
                                                        </Text>
                                                        <NullableTextField
                                                            error={this.props.errors.get(
                                                                'customControlData.gonioscopie_toelichting_os'
                                                            )}
                                                            expandableTextArea
                                                            fieldKey="gonioscopie_toelichting_os"
                                                            label=""
                                                            placeholder="Toelichting..."
                                                        />
                                                    </StyledBorderAndPadding>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </>
                                )}
                            </>
                        )}
                        <>
                            {this.isExtraExaminationSelected('aanvullend_onderzoek') &&
                                this.showAdditionalExaminations()}

                            {this.anyOptionChecked() && (
                                <FormGroup
                                    hasErrors={this.props.errors.has('remark')}
                                    label={translateKey('diagnosticReport.remarks')}
                                >
                                    <InputTextArea
                                        automation="remark"
                                        error={this.props.errors.get('remark')}
                                        onChange={async (value) => await this.props.handleChange('remark', value)}
                                        onDoValidate={async () => await this.props.validateOne('remark')}
                                        placeholder={translateKey('common.remarksOrQuestions')}
                                        value={this.props.procedure.remark}
                                    />
                                </FormGroup>
                            )}
                        </>
                    </>
                )}
            </>
        );
    }

    private showAdditionalExaminations() {
        const additionalExaminationsOd = [...this.props.procedure.customControlData.aanvullend_onderzoek_od];
        const additionalExaminationsOs = [...this.props.procedure.customControlData.aanvullend_onderzoek_os];

        return (
            <>
                {this.state.additionalExaminations.map((onderzoek: string | null, index: number) => {
                    return (
                        <Fragment key={`additionalExamination${index}`}>
                            <FormGroup
                                hasErrors={this.props.errors.has(`customControlData.aanvullend_onderzoek.${index}`)}
                                label="Ander onderzoek"
                            >
                                <NullableInputText
                                    error={this.props.errors.get(`customControlData.aanvullend_onderzoek.${index}`)}
                                    onChange={async (value) =>
                                        await this.handleChangeAdditionalExaminations(value, index)
                                    }
                                    onDoValidate={async () =>
                                        await this.props.validateOneCustomControl('aanvullend_onderzoek', index)
                                    }
                                    placeholder="Naam van het onderzoek"
                                    value={onderzoek || ''}
                                />
                            </FormGroup>

                            {onderzoek && (
                                <FormGroup
                                    hasErrors={
                                        this.props.errors.has(`customControlData.aanvullend_onderzoek_od.${index}`) ||
                                        this.props.errors.has(`customControlData.aanvullend_onderzoek_os.${index}`)
                                    }
                                >
                                    <Row columnGap={[2, 4, 6]}>
                                        <Col span={[12, 6, null, null, 4]}>
                                            <StyledBorderAndPadding>
                                                <Text
                                                    fontWeight="bold"
                                                    size="xs"
                                                >
                                                    OD
                                                </Text>
                                                <NullableInputText
                                                    error={this.props.errors.get(
                                                        `customControlData.aanvullend_onderzoek_od.${index}`
                                                    )}
                                                    expandableTextArea
                                                    onChange={async (value) => {
                                                        for (let i = 0; i < index; i++) {
                                                            if (additionalExaminationsOd[i] === undefined) {
                                                                additionalExaminationsOd[i] = null;
                                                            }
                                                        }
                                                        additionalExaminationsOd[index] = value;
                                                        await this.props.handleChangeCustomControl({
                                                            aanvullend_onderzoek_od: additionalExaminationsOd,
                                                        });
                                                    }}
                                                    onDoValidate={async () => {
                                                        await this.props.validateOneCustomControl(
                                                            'aanvullend_onderzoek_od',
                                                            index
                                                        );
                                                    }}
                                                    placeholder="Bevindingen"
                                                    value={additionalExaminationsOd[index] || ''}
                                                />
                                            </StyledBorderAndPadding>
                                        </Col>
                                        <Col span={[12, 6, null, null, 4]}>
                                            <StyledBorderAndPadding>
                                                <Text
                                                    fontWeight="bold"
                                                    size="xs"
                                                >
                                                    OS
                                                </Text>
                                                <NullableInputText
                                                    error={this.props.errors.get(
                                                        `customControlData.aanvullend_onderzoek_os.${index}`
                                                    )}
                                                    expandableTextArea
                                                    onChange={async (value) => {
                                                        for (let i = 0; i < index; i++) {
                                                            if (additionalExaminationsOs[i] === undefined) {
                                                                additionalExaminationsOs[i] = null;
                                                            }
                                                        }
                                                        additionalExaminationsOs[index] = value;
                                                        await this.props.handleChangeCustomControl({
                                                            aanvullend_onderzoek_os: additionalExaminationsOs,
                                                        });
                                                    }}
                                                    onDoValidate={async () => {
                                                        await this.props.validateOneCustomControl(
                                                            'aanvullend_onderzoek_os',
                                                            index
                                                        );
                                                    }}
                                                    placeholder="Bevindingen"
                                                    value={additionalExaminationsOs[index] || ''}
                                                />
                                            </StyledBorderAndPadding>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}
                        </Fragment>
                    );
                })}
            </>
        );
    }

    private isExtraExaminationSelected<K extends keyof ITooExtraExamination>(...keys: K[]) {
        let result = false;

        if (keys && keys.length > 0) {
            for (const key of keys) {
                result = result || !!this.state.examinations[key] || !!this.state.examinationsLinkedToReport[key];
            }
        } else {
            result =
                this.state.examinations.eigen_correctie ||
                this.state.examinations.autorefractie ||
                this.state.examinations.cycloplegische ||
                (this.state.examinations.nabije_visus && this.state.examinationsLinkedToReport.nabije_visus) ||
                this.state.examinations.break_up_time ||
                (this.state.examinations.amsler_test && this.state.examinationsLinkedToReport.amsler_test) ||
                (this.state.examinations.pachymetrie && this.state.examinationsLinkedToReport.pachymetrie) ||
                (this.state.examinations.gonioscopie && this.state.examinationsLinkedToReport.gonioscopie) ||
                this.state.examinations.aanvullend_onderzoek;
        }

        return result;
    }

    private isExtraExaminationOpen() {
        return (
            this.state.examinationsLinkedToReport.nabije_visus ||
            this.state.examinationsLinkedToReport.amsler_test ||
            this.state.examinationsLinkedToReport.pachymetrie ||
            this.state.examinationsLinkedToReport.gonioscopie
        );
    }

    private anyOptionChecked<K extends keyof ITooProcedureCustomControlData>(...keys: K[]) {
        let fields;
        if (keys && keys.length > 0) {
            fields = keys;
        } else {
            fields = [
                'eigen_correctie_sferisch_od',
                'eigen_correctie_cylindrish_od',
                'eigen_correctie_as_od',
                'eigen_correctie_sferisch_os',
                'eigen_correctie_cylindrish_os',
                'eigen_correctie_as_os',
                'autorefractie_sferisch_od',
                'autorefractie_cylindrisch_od',
                'autorefractie_as_od',
                'autorefractie_sferisch_os',
                'autorefractie_cylindrisch_os',
                'autorefractie_as_os',
                'cycloplegische_sferisch_od',
                'cycloplegische_cylindrisch_od',
                'cycloplegische_as_od',
                'cycloplegische_sferisch_os',
                'cycloplegische_cylindrisch_os',
                'cycloplegische_as_os',
                'nabije_visus_od',
                'nabije_visus_os',
                'break_up_time_od',
                'break_up_time_os',
                'amsler_test_od',
                'amsler_test_os',
                'pachymetrie_od',
                'pachymetrie_os',
                'gonioscopie_od',
                'gonioscopie_os',
                'aanvullend_onderzoek',
            ];
        }

        return (
            fields.find((field) => {
                if (field !== 'aanvullend_onderzoek') {
                    return this.props.customControlData[field as K] !== null;
                } else {
                    return (
                        this.props.customControlData.aanvullend_onderzoek !== null &&
                        this.props.customControlData.aanvullend_onderzoek.length > 0 &&
                        this.props.customControlData.aanvullend_onderzoek[0] !== null
                    );
                }
            }) !== undefined
        );
    }

    private isRequiredExamination<K extends keyof ITooExtraExamination>(examination: K) {
        let result: boolean = false;

        switch (examination) {
            case 'nabije_visus':
                result =
                    this.props.protocol === TooProtocol.cataract ||
                    (this.props.requiredFields !== undefined && !!this.props.requiredFields.nabije_visus);
                break;
            case 'pachymetrie':
                result =
                    this.props.protocol === TooProtocol.glaucoom ||
                    (this.props.requiredFields !== undefined && !!this.props.requiredFields.pachymetrie);
                break;
            case 'amsler_test':
                result =
                    this.props.protocol === TooProtocol.maculaDegeneratie ||
                    (this.props.requiredFields !== undefined && !!this.props.requiredFields.amsler_test);
                break;
            case 'gonioscopie':
                result =
                    this.props.protocol === TooProtocol.glaucoom ||
                    (this.props.requiredFields !== undefined && !!this.props.requiredFields.gonioscopie);
                break;
            default:
                result = false;
        }

        return result;
    }

    private requiredExaminationHasErrors<K extends keyof TooV1ProcedureRequiredFields>(examination: K) {
        return (
            this.props.errors.has(`customControlData.${examination}_od`) ||
            this.props.errors.has(`customControlData.${examination}_os`)
        );
    }

    private async handleExaminationSelected<K extends keyof ITooExtraExamination>(examination: K) {
        const update = {
            examinations: this.state.examinations,
            examinationsLinkedToReport: {
                ...this.state.examinationsLinkedToReport,
                [examination]:
                    this.state.examinationsLinkedToReport[examination] && this.isRequiredExamination(examination)
                        ? true
                        : !this.state.examinationsLinkedToReport[examination],
            },
        };

        if (!this.isRequiredExamination(examination)) {
            update.examinations = {
                ...this.state.examinations,
                [examination]: !this.state.examinations[examination],
            };

            if (!update.examinations[examination]) {
                await this.resetExamination(examination);
            }

            if (!this.isExtraExaminationSelected()) {
                await this.props.handleChange('remark', null);
            }
        }

        await this.setState(update);
    }

    private async handleChangeAdditionalExaminations(value: string | null, index: number) {
        const additionalExaminationsOd = [...this.props.procedure.customControlData.aanvullend_onderzoek_od];
        const additionalExaminationsOs = [...this.props.procedure.customControlData.aanvullend_onderzoek_os];

        const updatedAdditionalExaminationsAndFindings: Partial<ITooProcedureCustomControlData> = {};

        let updatedAdditionalExaminations = [...this.state.additionalExaminations];
        updatedAdditionalExaminations[index] = value;
        updatedAdditionalExaminations = updatedAdditionalExaminations.filter((item) => item !== null);

        updatedAdditionalExaminationsAndFindings.aanvullend_onderzoek = updatedAdditionalExaminations as string[];

        if (!value) {
            additionalExaminationsOd.splice(index, 1);
            additionalExaminationsOs.splice(index, 1);

            updatedAdditionalExaminationsAndFindings.aanvullend_onderzoek_od = additionalExaminationsOd;
            updatedAdditionalExaminationsAndFindings.aanvullend_onderzoek_os = additionalExaminationsOs;
        }

        await this.props.handleChangeCustomControl(updatedAdditionalExaminationsAndFindings);
        await this.setState({ additionalExaminations: updatedAdditionalExaminations });

        if (!value) {
            await Promise.all(
                this.props.customControlData.aanvullend_onderzoek.map(async (_, i) => {
                    this.props.validateOneCustomControl('aanvullend_onderzoek_od', i);
                    this.props.validateOneCustomControl('aanvullend_onderzoek_os', i);
                })
            );
        }
    }

    private getExaminationActiveStatus() {
        return {
            eigen_correctie: this.anyOptionChecked(
                ...this.getProcedureCustomControlDataExtraExaminationKeys('eigen_correctie')
            ),
            autorefractie: this.anyOptionChecked(
                ...this.getProcedureCustomControlDataExtraExaminationKeys('autorefractie')
            ),
            cycloplegische: this.anyOptionChecked(
                ...this.getProcedureCustomControlDataExtraExaminationKeys('cycloplegische')
            ),
            ...this.getExaminationLinkedWithReportActiveStatus(),
        };
    }

    private getExaminationLinkedWithReportActiveStatus() {
        return {
            nabije_visus:
                this.isRequiredExamination('nabije_visus') ||
                this.anyOptionChecked('nabije_visus_od', 'nabije_visus_os'),
            break_up_time: this.anyOptionChecked('break_up_time_od', 'break_up_time_os'),
            amsler_test:
                this.isRequiredExamination('amsler_test') || this.anyOptionChecked('amsler_test_od', 'amsler_test_os'),
            pachymetrie:
                this.isRequiredExamination('pachymetrie') || this.anyOptionChecked('pachymetrie_od', 'pachymetrie_os'),
            gonioscopie:
                this.isRequiredExamination('gonioscopie') || this.anyOptionChecked('gonioscopie_od', 'gonioscopie_os'),
            aanvullend_onderzoek: this.anyOptionChecked('aanvullend_onderzoek'),
        };
    }

    private checkRequiredExaminationsFromProtocol(protocol: TooProtocol) {
        const requiredExaminationsOpen = this.state.examinationsLinkedToReport;
        switch (protocol) {
            case TooProtocol.glaucoom:
                requiredExaminationsOpen.pachymetrie = true;
                requiredExaminationsOpen.gonioscopie = true;
                break;
            case TooProtocol.cataract:
                requiredExaminationsOpen.nabije_visus = true;
                break;
            case TooProtocol.maculaDegeneratie:
                requiredExaminationsOpen.amsler_test = true;
                break;
            default:
                return this.state.examinationsLinkedToReport;
        }

        return requiredExaminationsOpen;
    }

    private async resetExamination<K extends keyof ITooExtraExamination>(examination: K) {
        const extraExaminationKeys = this.getProcedureCustomControlDataExtraExaminationKeys(examination);
        const initializedCustomControlData: ITooProcedureCustomControlData =
            tooV1ProcedureInitializeCustomControlData();
        const values = {
            ...omit(
                initializedCustomControlData,
                Object.keys(initializedCustomControlData).filter(
                    (key) => !extraExaminationKeys.includes(key as keyof ITooProcedureCustomControlData)
                )
            ),
        };

        await this.props.handleChangeCustomControl(values);

        await Promise.all(
            extraExaminationKeys.map((key) => {
                this.props.validateOneCustomControl(key);
            })
        );

        if (examination === 'aanvullend_onderzoek') {
            await this.setState({ additionalExaminations: [] });
        }
    }

    private async resetOldAmslerTestValues() {
        if (
            this.props.customControlData.amsler_test_od &&
            !Object.values(TooAmslerTest).includes(this.props.customControlData.amsler_test_od as TooAmslerTest)
        ) {
            await this.props.handleChangeCustomControl({ amsler_test_od: null });
        }

        if (
            this.props.customControlData.amsler_test_os &&
            !Object.values(TooAmslerTest).includes(this.props.customControlData.amsler_test_os as TooAmslerTest)
        ) {
            await this.props.handleChangeCustomControl({ amsler_test_os: null });
        }
    }

    private getProcedureCustomControlDataExtraExaminationKeys<K extends keyof ITooExtraExamination>(examination: K) {
        return Object.keys(this.props.procedure.customControlData).filter((key) =>
            key.includes(examination)
        ) as (keyof ITooProcedureCustomControlData)[];
    }
}
